import React, { useEffect, useRef } from 'react';

interface PurchaseCelebrationProps {
  /** Credits granted, in microdollars. Null while the webhook has not landed. */
  creditsMicros: number | null;
  /** Balance after the grant, in microdollars. */
  balanceMicros: number | null;
  /** True while we are still waiting on Stripe's webhook. */
  pending: boolean;
  onDismiss: () => void;
}

function formatMicros(micros: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(micros / 1_000_000);
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  rotation: number;
  spin: number;
  color: string;
}

/**
 * One confetti burst on a real event — money actually cleared.
 *
 * Deliberately a single burst rather than a loop: the motion marks a state
 * change, it is not decoration. Skipped entirely under prefers-reduced-motion,
 * where the card alone carries the message.
 */
function useConfetti(active: boolean) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    // Pull the accent straight from the theme so the burst matches light/dark.
    const root = getComputedStyle(document.documentElement);
    const accent = root.getPropertyValue('--accent').trim() || '92 124 250';
    const palette = [
      `rgb(${accent})`,
      'rgb(255 212 96)',
      'rgb(126 217 155)',
      'rgb(244 143 177)',
      'rgb(147 197 253)',
    ];

    const particles: Particle[] = Array.from({ length: 140 }, () => {
      const angle = (Math.PI * 2 * Math.random());
      const speed = 3 + Math.random() * 7;
      return {
        x: width / 2,
        y: height * 0.38,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 3,
        size: 5 + Math.random() * 6,
        rotation: Math.random() * Math.PI,
        spin: (Math.random() - 0.5) * 0.3,
        color: palette[Math.floor(Math.random() * palette.length)],
      };
    });

    let frame = 0;
    let raf = 0;
    const totalFrames = 150;

    const tick = () => {
      frame += 1;
      ctx.clearRect(0, 0, width, height);
      const fade = Math.max(0, 1 - frame / totalFrames);

      for (const p of particles) {
        p.vy += 0.22; // gravity
        p.vx *= 0.99;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.spin;

        ctx.save();
        ctx.globalAlpha = fade;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx.restore();
      }

      if (frame < totalFrames) {
        raf = requestAnimationFrame(tick);
      } else {
        ctx.clearRect(0, 0, width, height);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active]);

  return canvasRef;
}

export function PurchaseCelebration({
  creditsMicros,
  balanceMicros,
  pending,
  onDismiss,
}: PurchaseCelebrationProps) {
  const canvasRef = useConfetti(!pending);
  const dismissRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    dismissRef.current?.focus();
  }, [pending]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="purchase-celebration-title"
      onClick={onDismiss}
    >
      <canvas
        ref={canvasRef}
        className="pointer-events-none absolute inset-0 h-full w-full"
        aria-hidden="true"
      />

      <div
        className="relative w-full max-w-md rounded-2xl border border-line-1 bg-surface-2 p-8 text-center shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {pending ? (
          <>
            <div className="mx-auto mb-5 h-14 w-14 animate-spin rounded-full border-2 border-line-1 border-t-accent" />
            <h2 id="purchase-celebration-title" className="text-xl font-semibold text-fg-1">
              Confirming your payment
            </h2>
            <p className="mt-2 text-sm text-fg-3">
              Stripe has your payment. We're waiting for it to clear — this usually takes a few seconds.
            </p>
          </>
        ) : (
          <>
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-accent/15">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-7 w-7 text-accent"
                aria-hidden="true"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>

            <h2 id="purchase-celebration-title" className="text-2xl font-semibold text-fg-1">
              Thank you!
            </h2>
            <p className="mt-2 text-sm text-fg-3">
              Your payment went through and your credits are ready to use.
            </p>

            {creditsMicros !== null && (
              <div className="mt-6 rounded-xl border border-line-1 bg-surface-3 p-4">
                <div className="font-mono text-3xl font-semibold text-accent">
                  +{formatMicros(creditsMicros)}
                </div>
                {balanceMicros !== null && (
                  <div className="mt-1 text-xs uppercase tracking-wider text-fg-3">
                    New balance {formatMicros(balanceMicros)}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <button
          ref={dismissRef}
          type="button"
          onClick={onDismiss}
          className="mt-7 w-full rounded-lg bg-accent px-4 py-2.5 font-medium text-accent-contrast transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-surface-2"
        >
          {pending ? 'Dismiss' : 'Back to writing'}
        </button>
      </div>
    </div>
  );
}
